import React from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import experiments from '@reverbdotcom/commons/src/experiments';
import { first } from 'lodash';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { reverb_search_ListingsSearchRequest_Sort } from '@reverbdotcom/commons/src/rql';
import { experimentInt } from '@reverbdotcom/commons/src/user_context_helpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { HomepageComponentTrackingNames } from '@reverbdotcom/commons/src/event_tracking/homepage';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import CmsListingsCollection, { ADDITIONAL_SORTS } from '@reverbdotcom/commons/src/cms/components/cms_listings_collection';
import { getItem } from '@reverbdotcom/commons/src/local_storage_utils';
import { US_CON } from '@reverbdotcom/commons/src/constants';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { BestSellers, Free2DayShipping } from '@reverbdotcom/commons/src/components/experimental_mosaics_row';
import { ExperimentalPromotedListingsRowQuery } from '../../../schemas/rql';
import { ChildProps } from 'react-apollo';

type Props = ChildProps<{}, ExperimentalPromotedListingsRowQuery.Query>;

export function InternalExperimentalPromotedListingsRow(props: Props) {
  const user = useUser();
  const { data } = props;
  const recentSearch: string | undefined = first(getItem('RECENT_SEARCHES', []));
  const topProductTypeUuids = data?.me?.profile?.topProductTypeUuids || [];
  const experiment = experiments.REPLACE_PROMOTED_LISTINGS_HOMEPAGE_ROW;

  const eligible = !data.loading && !!recentSearch && topProductTypeUuids.length > 0;

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: 'ExperimentalPromotedListingsRow',
    experiments: formatExperimentsForEventAttributes(user, [experiment]),
  }, eligible);

  const control = (
    <CmsListingsCollection
      componentName={HomepageComponentTrackingNames.HomepageBumpedListings}
      title={I18n.t('discovery.bumpsView.promotedListings')}
      sort={ADDITIONAL_SORTS.BUMP_BOOST}
      ssr={false}
      maxCount={12}
      largeTiles
    />
  );

  if (!eligible) { return control; }

  const expGroup = experimentInt(user, experiment);
  if (expGroup === 1) {
    return <BestSellers />;
  } else if (expGroup === 2 && user.shippingRegionCode === US_CON) {
    return <Free2DayShipping />;
  } else if (expGroup === 3 && user.countryCode === 'US') {
    return (
      <CmsListingsCollection
        title={I18n.t('discovery.homepage.zeroPercentFinancingAvailable')}
        trackingName="HomepageZeroPercentFinancing"
        acceptsPaymentPlans
        userShippingRegion={user.shippingRegionCode}
        priceMin={'500'}
        priceMax={'9999'}
        ctaTargetHref={'/c/financing'}
        sort={reverb_search_ListingsSearchRequest_Sort.WATCHERS_COUNT_DESC}
        maxCount={12}
        largeTiles
        ssr={false}
      />
    );
  } else if (expGroup === 4) {
    return (
      <CmsListingsCollection
        ctaTargetHref={Paths.marketplace.expand({ query: recentSearch })}
        query={recentSearch}
        title={I18n.t('discovery.homepage.fromYourRecentSearch', { recentSearch })}
        trackingName="HomepageFromYourRecentSearch"
        userShippingRegion={user.shippingRegionCode}
        maxCount={12}
        largeTiles
        ssr={false}
      />
    );
  } else if (expGroup === 5) {
    return (
      <CmsListingsCollection
        ctaTargetHref={Paths.marketplace.expand({ sort: 'published_at|desc' })}
        categoryUuids={props?.data?.me?.profile?.topProductTypeUuids}
        sort={reverb_search_ListingsSearchRequest_Sort.PUBLISHED_AT_DESC}
        title={I18n.t('discovery.homepage.justListed')}
        trackingName="HomepageJustListed"
        userShippingRegion={user.shippingRegionCode}
        maxCount={12}
        largeTiles
        ssr={false}
      />
    );
  }

  return control;
}

const withQuery = withGraphql<Props, ExperimentalPromotedListingsRowQuery.Query>(
  gql`
    query Experimental_Promoted_Listings_Row_Query {
      me {
        uuid
        profile {
          topProductTypeUuids
        }
      }
    }
  `,
  {
    options: {
      ssr: false,
    },
  },
);

export const ExperimentalPromotedListingsRow = withQuery(InternalExperimentalPromotedListingsRow);
